import type { AcademyLesson } from '@/payload-types'

/**
 * Pure, server-free helpers for deriving Academy course meta (lesson count,
 * summed reading time, difficulty label). Kept separate from getAcademyCourse.ts
 * so they can be imported by client/server components and unit-tested without
 * pulling in Payload or next/headers.
 */

/** A lesson projected down to the fields the course index needs (no content blob). */
export type AcademyLessonSummary = Pick<
  AcademyLesson,
  'id' | 'slug' | 'title' | 'snippet' | 'readingTimeMinutes'
>

const DIFFICULTY_LABELS: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
}

/**
 * Reorder a course's lessons by the sequence defined on the course (`lessonOrder`,
 * a list of lesson IDs or populated lessons). Lessons not named in the order are
 * appended in their incoming order (creation order). Mirrors groupCoursesByDifficulty.
 */
export function orderLessonsByCourse<T extends { id: string }>(
  lessons: T[],
  lessonOrder: (string | { id: string })[] | null | undefined,
): T[] {
  const orderIds = (lessonOrder || [])
    .map((entry) => (typeof entry === 'object' ? entry.id : entry))
    .filter((id): id is string => Boolean(id))

  if (orderIds.length === 0) return lessons

  const byId = new Map(lessons.map((lesson) => [lesson.id, lesson]))
  const ordered: T[] = []
  for (const id of orderIds) {
    const lesson = byId.get(id)
    if (lesson) {
      ordered.push(lesson)
      byId.delete(id)
    }
  }
  // Append any lessons not named in the order, preserving incoming (creation) order.
  return [...ordered, ...lessons.filter((lesson) => byId.has(lesson.id))]
}

/** "beginner" -> "Beginner". Returns '' for empty/unknown values. */
export function formatDifficultyLabel(difficulty?: string | null): string {
  if (!difficulty) return ''
  return DIFFICULTY_LABELS[difficulty] || difficulty
}

export interface CourseMetaInput {
  count: number
  totalMinutes: number
  difficulty?: string | null
}

/**
 * Derive count + summed reading time from a list of lessons.
 */
export function getCourseMeta(
  lessons: Pick<AcademyLesson, 'readingTimeMinutes'>[],
): { count: number; totalMinutes: number } {
  const count = lessons.length
  const totalMinutes = lessons.reduce((sum, lesson) => sum + (lesson.readingTimeMinutes || 0), 0)
  return { count, totalMinutes }
}

export interface LessonNavigation<T> {
  /** Position of the current lesson in the ordered list, or -1 if absent. */
  currentIndex: number
  /** The lesson after the current one, or null when last/absent. */
  nextLesson: T | null
  /** True only when the current lesson is the final one in the course. */
  isLast: boolean
}

/**
 * Locate a lesson within its course's ordered lesson list (see orderLessonsByCourse)
 * and derive navigation. A lesson missing from the list (`currentIndex === -1`) yields
 * no next lesson and `isLast: false`, so neither the next-lesson strip nor the
 * end-of-course block renders — safe degradation rather than a broken link.
 */
export function getLessonNavigation<T extends { slug?: string | null }>(
  lessons: T[],
  lessonSlug: string,
): LessonNavigation<T> {
  const currentIndex = lessons.findIndex((lesson) => lesson.slug === lessonSlug)
  const isLast = currentIndex >= 0 && currentIndex === lessons.length - 1
  const nextLesson =
    currentIndex >= 0 && currentIndex < lessons.length - 1 ? lessons[currentIndex + 1] : null
  return { currentIndex, nextLesson, isLast }
}

/**
 * Format the hero/card meta line, e.g. "5 lessons · 50 min · Beginner".
 * Reading time is shown as a rounded whole number of minutes (no "~" prefix).
 * Difficulty is appended only when provided. Returns "Coming soon" with no lessons.
 */
export function formatCourseMeta({ count, totalMinutes, difficulty }: CourseMetaInput): string {
  if (count === 0) return 'Coming soon'

  const parts = [`${count} lesson${count === 1 ? '' : 's'}`]
  if (totalMinutes > 0) parts.push(`${Math.round(totalMinutes)} min`)

  const label = formatDifficultyLabel(difficulty)
  if (label) parts.push(label)

  return parts.join(' · ')
}
